import { ReactNode } from "react";
import { Lock, Sparkles } from "lucide-react";
import { Navigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { usePlanEntitlements, EntitlementKey, PlanCode, CRM_ENTITLEMENTS } from "@/hooks/usePlanEntitlements";
import { CrmDisabledNotice, CrmDisabledRoute } from "./CrmDisabledNotice";

const REQUIRED_PLAN: Record<EntitlementKey, PlanCode> = {
  customMenus: "pro",
  interactiveQr: "pro",
  feedbackCustom: "pro",
  integrations: "pro",
  basicCrm: "pro",
  fullCrm: "max",
  offers: "max",
  loyalty: "max",
  crm: "mini",
};

const PLAN_LABEL: Record<PlanCode, string> = {
  mini: "Mini",
  pro: "Pro",
  max: "Max",
};

export interface GatedProps {
  feature: EntitlementKey;
  /**
   * hidden — render nothing when not entitled.
   * locked — render a locked teaser card with upgrade CTA.
   * custom — render the `fallback` prop instead.
   */
  mode?: "hidden" | "locked" | "custom";
  fallback?: ReactNode;
  children: ReactNode;
}

export function Gated({ feature, mode = "hidden", fallback, children }: GatedProps) {
  const { entitlements, isLoading, crmDisabled } = usePlanEntitlements();
  if (isLoading) return null;
  if (entitlements[feature]) return <>{children}</>;
  if (mode === "custom") return <>{fallback ?? null}</>;
  if (mode === "hidden") return null;
  // Switched off for the outlet by Dhaba support: no plan to upgrade to.
  if (crmDisabled && CRM_ENTITLEMENTS.has(feature)) return <CrmDisabledNotice />;
  return <LockedTeaser feature={feature} />;
}

function LockedTeaser({ feature }: { feature: EntitlementKey }) {
  const navigate = useNavigate();
  const required = REQUIRED_PLAN[feature];
  return (
    <Card className="border-dashed bg-muted/30">
      <CardContent className="flex flex-col items-center justify-center py-10 text-center gap-3">
        <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
          <Lock className="h-6 w-6 text-amber-600" />
        </div>
        <div>
          <h3 className="text-base font-semibold flex items-center justify-center gap-1.5">
            Available on {PLAN_LABEL[required]}
            <Sparkles className="h-3.5 w-3.5 text-amber-500" />
          </h3>
          <p className="text-sm text-muted-foreground max-w-md mt-1">
            Upgrade your plan to unlock this feature.
          </p>
        </div>
        <Button size="sm" onClick={() => navigate("/settings/account")}>
          View plans
        </Button>
      </CardContent>
    </Card>
  );
}

export interface RequirePlanProps {
  feature: EntitlementKey;
  children: ReactNode;
  /** The route already sits in a layout shell: render the CRM-off notice alone. */
  nested?: boolean;
}

/**
 * Route-level guard: renders children if entitled. A CRM screen while CRM is
 * switched off for the outlet shows the "CRM is turned off" notice; any other
 * missing entitlement redirects to /settings/account (plans).
 */
export function RequirePlan({ feature, children, nested }: RequirePlanProps) {
  const { entitlements, isLoading, crmDisabled } = usePlanEntitlements();
  if (isLoading) return null;
  if (entitlements[feature]) return <>{children}</>;
  if (crmDisabled && CRM_ENTITLEMENTS.has(feature)) return <CrmDisabledRoute nested={nested} />;
  return <Navigate to="/settings/account" replace />;
}
